import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BellRing, Save } from "lucide-react";
import { toast } from "sonner";

const SEV_LABEL = { info: "Info", warning: "Advertencia", critical: "Crítica" };

export default function NotificationsCard() {
  const { user, fetchMe } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setPrefs({
      email_notifications_enabled: user.email_notifications_enabled ?? true,
      notification_scope: user.notification_scope ?? "all",
      notification_property_ids: user.notification_property_ids ?? [],
      notification_severities: user.notification_severities ?? ["warning", "critical"],
    });
    api.get("/properties").then((r) => setProperties(r.data));
  }, [user]);

  if (!prefs) return null;

  const toggleSev = (s) => {
    const cur = new Set(prefs.notification_severities);
    if (cur.has(s)) cur.delete(s); else cur.add(s);
    setPrefs({ ...prefs, notification_severities: Array.from(cur) });
  };

  const togglePropId = (pid) => {
    const cur = new Set(prefs.notification_property_ids);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    setPrefs({ ...prefs, notification_property_ids: Array.from(cur) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/me/notifications", prefs);
      await fetchMe();
      toast.success("Preferencias guardadas");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 sm:p-6 space-y-4" data-testid="notifications-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><BellRing className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Mis notificaciones por email</div>
          <div className="text-xs text-muted-foreground">
            Elegí qué alertas querés recibir en <span className="text-foreground">{user.email}</span>.
          </div>
        </div>
        <Switch
          checked={prefs.email_notifications_enabled}
          onCheckedChange={(v) => setPrefs({ ...prefs, email_notifications_enabled: v })}
          data-testid="notifications-toggle"
        />
      </div>

      {prefs.email_notifications_enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Alcance</div>
              <Select
                value={prefs.notification_scope}
                onValueChange={(v) => setPrefs({ ...prefs, notification_scope: v })}
              >
                <SelectTrigger data-testid="notifications-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las propiedades</SelectItem>
                  <SelectItem value="properties">Solo propiedades seleccionadas</SelectItem>
                  <SelectItem value="none">Nada (solo in-app)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Severidad</div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(SEV_LABEL).map(([k, l]) => {
                  const active = prefs.notification_severities.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleSev(k)}
                      data-testid={`notifications-sev-${k}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "bg-foreground text-background border-foreground"
                          : "bg-transparent text-muted-foreground border-border/60 hover:border-foreground/40"
                      }`}
                    >{l}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {prefs.notification_scope === "properties" && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Propiedades incluidas</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {properties.map((p) => {
                  const active = prefs.notification_property_ids.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        active ? "border-foreground/40 bg-secondary" : "border-border/60 hover:border-foreground/20"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-foreground"
                        checked={active}
                        onChange={() => togglePropId(p.id)}
                        data-testid={`notifications-prop-${p.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground">{p.country} · {p.city || p.address}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <div className="pt-2">
        <Button size="sm" onClick={save} disabled={saving} data-testid="notifications-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar preferencias"}
        </Button>
      </div>
    </div>
  );
}
